<script>
  import { createOffer } from '../lib/offers.js';
  import dagre from 'dagre';
  import { workspace, callBbAi, refreshWorkspace } from '../lib/stores/workspace.js';
  import { supabase } from '../lib/supabase.js';
  import { showToast } from '../lib/stores/toast.js';
  import EntityEditor from './EntityEditor.svelte';
  import StepEditor from './StepEditor.svelte';
  import MapAddMenu from './MapAddMenu.svelte';
  import ChainBuilder from './ChainBuilder.svelte';
  import TechMapShell from './TechMapShell.svelte';
  import { techMode } from '../lib/stores/techMode.svelte.js';
  import { ui } from '../lib/stores/ui.svelte.js';

  $effect(() => {
    if (ui.techMode) techMode.setTech();
    else techMode.setNormal();
  });

  let selectedChainId = $state(null);
  let selectedSegment = $state(null); // { kind, id }
  let pan = $state({ x: 0, y: 0 });
  let zoom = $state(1);
  let dragging = $state(false);
  let dragStart = $state({ x: 0, y: 0 });
  let panStart = $state({ x: 0, y: 0 });

  // Click-vs-drag pending state — only promotes to drag after 5px movement
  const DRAG_THRESHOLD = 5;
  let pendingNodeAction = $state(null); // { node, startClientX, startClientY, nodeX, nodeY, started }
  let draggingNode = $state(null); // { id, kind, offsetX, offsetY, currentX, currentY }
  let expandedSteps = $state(new Set());

  // Audit
  let auditResult = $state(null);
  let auditLoading = $state(false);

  // ChainBuilder + arrow mode
  let showChainBuilder = $state(false);
  let chainBuilderInitial = $state({ audienceId: null, dealId: null });
  let arrowMode = $state(null); // { firstId, firstKind } | null
  let placingSegment = $state(null); // { kind, draftId } | null

  // Perspective filter — pick 1-3 segments, dim everything not on a path through ALL of them
  let perspectiveMode = $state(false);
  let perspectiveSelection = $state([]); // [{ kind, id }]

  // Fan-on-hover for clusters
  let hoveredFanCenter = $state(null); // node.id

  // Outline modal
  let outlineModalOpen = $state(false);
  let outlineText = $state('');
  let outlineLoading = $state(false);

  // Multi-select mode
  let selectMode = $state(false);
  let selectedSegments = $state([]); // [{ kind, id }]
  let deletingSelected = $state(false);
  let multiMoveSnapshot = $state(null); // { anchorId, anchorStart, others: [{kind,id,startX,startY}] }

  function toggleSelectMode() {
    selectMode = !selectMode;
    if (!selectMode) selectedSegments = [];
  }

  function isSelected(kind, id) {
    return selectedSegments.some(s => s.kind === kind && s.id === id);
  }

  function toggleSegmentSelection(kind, id) {
    if (isSelected(kind, id)) {
      selectedSegments = selectedSegments.filter(s => !(s.kind === kind && s.id === id));
    } else {
      selectedSegments = [...selectedSegments, { kind, id }];
    }
  }

  async function deleteSelectedSegments() {
    if (selectedSegments.length === 0) return;
    if (!confirm(`Discard ${selectedSegments.length} segment${selectedSegments.length === 1 ? '' : 's'}?`)) return;
    deletingSelected = true;
    try {
      for (const seg of selectedSegments) {
        const table = seg.kind === 'audience' ? 'bb_audiences'
                    : seg.kind === 'deal' ? 'bb_deals'
                    : 'bb_steps';
        await supabase.from(table).update({ deleted_at: new Date().toISOString() }).eq('id', seg.id);
      }
      await refreshWorkspace();
      showToast(`Discarded ${selectedSegments.length} segment${selectedSegments.length === 1 ? '' : 's'}`, 'success');
      selectedSegments = [];
      selectMode = false;
    } catch (e) {
      showToast('Delete failed: ' + e.message, 'error');
    } finally {
      deletingSelected = false;
    }
  }

  // Parse outline text like:
  //   "x leads to y leads to z"
  //   "b and d are also connected to z"
  // Returns { labels: string[], edges: [from, to][] }
  function parseOutlineText(text) {
    const labels = new Map(); // lowercased -> original
    const edges = [];
    const addLabel = (raw) => {
      const trimmed = raw.trim().replace(/^[,;.]+|[,;.]+$/g, '').trim();
      if (!trimmed) return null;
      const key = trimmed.toLowerCase();
      if (!labels.has(key)) labels.set(key, trimmed);
      return labels.get(key);
    };
    const splitGroup = (chunk) =>
      chunk.split(/\s*(?:,|&|\band\b|\bor\b)\s*/i).map(s => s.trim()).filter(Boolean);

    const sentences = text.split(/[\n.;]+/).map(s => s.trim()).filter(Boolean);
    for (const sentence of sentences) {
      // Split on relationship phrases
      const parts = sentence.split(/\s+(?:leads? to|connects? to|connected to|→|->|=>|then|goes? to|flows? to)\s+/i);
      if (parts.length < 2) {
        // No relation — still register labels
        splitGroup(sentence).forEach(addLabel);
        continue;
      }
      // Strip leading "also" / filler
      const cleaned = parts.map(p => p.replace(/^\s*(also|are|is|then|next)\s+/i, '').trim());
      for (let i = 0; i < cleaned.length - 1; i++) {
        const fromGroup = splitGroup(cleaned[i]).map(addLabel).filter(Boolean);
        const toGroup = splitGroup(cleaned[i + 1]).map(addLabel).filter(Boolean);
        for (const f of fromGroup) for (const t of toGroup) {
          if (f.toLowerCase() !== t.toLowerCase()) edges.push([f, t]);
        }
      }
    }
    return { labels: [...labels.values()], edges };
  }

  async function submitOutline() {
    const trimmed = outlineText.trim();
    if (!trimmed || outlineLoading) return;
    outlineLoading = true;
    try {
      const { labels, edges } = parseOutlineText(trimmed);
      if (labels.length === 0) {
        showToast('Could not parse any segments from that outline', 'error');
        return;
      }

      // Resolve labels: reuse existing steps in workspace by case-insensitive name match,
      // otherwise create new draft steps.
      const labelToStepId = new Map();
      const existingSteps = (workspace.steps || []).filter(s => !s.deleted_at);
      for (const label of labels) {
        const match = existingSteps.find(s => (s.step_name || '').toLowerCase() === label.toLowerCase());
        if (match) {
          labelToStepId.set(label.toLowerCase(), match.id);
          continue;
        }
        const { data, error } = await supabase.from('bb_steps').insert({
          workspace_id: workspace.id,
          step_name: label,
          status: 'draft',
          state: 'user'
        }).select().single();
        if (error) throw error;
        labelToStepId.set(label.toLowerCase(), data.id);
      }

      let createdConns = 0;
      const chainId = selectedChain?.id || null;
      for (const [from, to] of edges) {
        const fromId = labelToStepId.get(from.toLowerCase());
        const toId = labelToStepId.get(to.toLowerCase());
        if (!fromId || !toId) continue;
        const { error } = await supabase.from('bb_segment_connections').insert({
          workspace_id: workspace.id,
          chain_id: chainId,
          from_segment_kind: 'step',
          from_segment_id: fromId,
          to_segment_kind: 'step',
          to_segment_id: toId
        });
        if (!error) createdConns++;
      }

      await refreshWorkspace();
      showToast(`Outline parsed: ${labels.length} segments, ${createdConns} connections`, 'success');
      outlineModalOpen = false;
      outlineText = '';
    } catch (e) {
      console.error('[outline] failed:', e);
      showToast('Outline failed: ' + (e?.message || String(e)), 'error');
    } finally {
      outlineLoading = false;
    }
  }

  function keyOfKindId(kind, id) { return `${kind}:${id}`; }

  const neighborMap = $derived.by(() => {
    const map = new Map();
    const conns = workspace.connections || [];
    for (const c of conns) {
      const fromKey = keyOfKindId(c.from_segment_kind, c.from_segment_id);
      const toKey = keyOfKindId(c.to_segment_kind, c.to_segment_id);
      if (!map.has(fromKey)) map.set(fromKey, new Set());
      if (!map.has(toKey)) map.set(toKey, new Set());
      map.get(fromKey).add(toKey);
      map.get(toKey).add(fromKey);
    }
    return map;
  });

  function neighborCountFor(node) {
    return neighborMap.get(keyOfKindId(node.kind, node.id))?.size || 0;
  }

  const fannedPositions = $derived.by(() => {
    if (!hoveredFanCenter) return new Map();
    const center = layout.nodes.find(n => n.id === hoveredFanCenter);
    if (!center) return new Map();
    const centerKey = keyOfKindId(center.kind, center.id);
    const neighborKeys = [...(neighborMap.get(centerKey) || [])];
    const neighbors = neighborKeys
      .map(k => layout.nodes.find(n => keyOfKindId(n.kind, n.id) === k))
      .filter(Boolean);
    if (neighbors.length === 0) return new Map();
    const radius = 200;
    const map = new Map();
    neighbors.forEach((n, i) => {
      const angle = (i / neighbors.length) * Math.PI * 2 - Math.PI / 2;
      map.set(n.id, {
        x: center.x + center.w / 2 - n.w / 2 + Math.cos(angle) * radius,
        y: center.y + center.h / 2 - n.h / 2 + Math.sin(angle) * radius
      });
    });
    return map;
  });

  function handleNodeMouseEnter(node) {
    if (arrowMode || perspectiveMode || placingSegment || draggingNode) return;
    if (neighborCountFor(node) >= 2) {
      hoveredFanCenter = node.id;
    }
  }
  function handleNodeMouseLeave() {
    hoveredFanCenter = null;
  }

  // Drag-to-merge / drag-to-split for step hierarchy
  const MERGE_HOLD_MS = 500;
  const SPLIT_BUFFER = 80;
  let mergeTargetId = $state(null);
  let mergeHoverStart = $state(0);
  let mergeTargetReady = $state(false);
  let mergeTimerRaf = null;

  function clearMergeState() {
    mergeTargetId = null;
    mergeHoverStart = 0;
    mergeTargetReady = false;
    if (mergeTimerRaf) {
      cancelAnimationFrame(mergeTimerRaf);
      mergeTimerRaf = null;
    }
  }

  function tickMergeReady() {
    if (mergeTargetId && mergeHoverStart) {
      const elapsed = performance.now() - mergeHoverStart;
      mergeTargetReady = elapsed >= MERGE_HOLD_MS;
      if (!mergeTargetReady) {
        mergeTimerRaf = requestAnimationFrame(tickMergeReady);
      } else {
        mergeTimerRaf = null;
      }
    } else {
      mergeTargetReady = false;
      mergeTimerRaf = null;
    }
  }

  function togglePerspectiveMode() {
    perspectiveMode = !perspectiveMode;
    if (!perspectiveMode) perspectiveSelection = [];
  }

  function isInPerspective(kind, id) {
    return perspectiveSelection.some(p => p.kind === kind && p.id === id);
  }

  function togglePerspectivePick(kind, id) {
    if (isInPerspective(kind, id)) {
      perspectiveSelection = perspectiveSelection.filter(p => !(p.kind === kind && p.id === id));
      return;
    }
    if (perspectiveSelection.length >= 3) {
      showToast('Max 3 segments in perspective. Remove one to add another.', 'info');
      return;
    }
    perspectiveSelection = [...perspectiveSelection, { kind, id }];
  }

  // Set of `${kind}:${id}` keys whose nodes pass the perspective filter, or null when off
  const perspectiveNodeIds = $derived.by(() => {
    if (!perspectiveMode || perspectiveSelection.length === 0) return null;
    const conns = workspace.connections || [];

    function reachableBoth(startKey) {
      const visited = new Set([startKey]);
      const queue = [startKey];
      while (queue.length) {
        const cur = queue.shift();
        for (const c of conns) {
          const fromKey = `${c.from_segment_kind}:${c.from_segment_id}`;
          const toKey = `${c.to_segment_kind}:${c.to_segment_id}`;
          if (fromKey === cur && !visited.has(toKey)) { visited.add(toKey); queue.push(toKey); }
          if (toKey === cur && !visited.has(fromKey)) { visited.add(fromKey); queue.push(fromKey); }
        }
      }
      return visited;
    }

    let intersection = null;
    for (const sel of perspectiveSelection) {
      const reach = reachableBoth(`${sel.kind}:${sel.id}`);
      if (!intersection) intersection = reach;
      else {
        const next = new Set();
        for (const k of intersection) if (reach.has(k)) next.add(k);
        intersection = next;
      }
    }
    return intersection || new Set();
  });

  const selectedChain = $derived(
    workspace.chains.find(c => c.id === selectedChainId) || workspace.chains[0]
  );

  function edgeKind(c) {
    if (c.from_segment_kind === 'audience') return 'entry';
    if (c.to_segment_kind === 'deal') return 'exit';
    return 'sequence';
  }

  // Build dagre graph for the selected chain
  const layout = $derived.by(() => {
    if (!selectedChain) return { nodes: [], edges: [], width: 800, height: 600 };

    const g = new dagre.graphlib.Graph({ compound: true });
    g.setGraph({ rankdir: 'TB', nodesep: 60, ranksep: 100, marginx: 40, marginy: 40 });
    g.setDefaultEdgeLabel(() => ({}));

    const NODE_W = 220, NODE_H = 96;

    const conns = selectedChain.connections || [];

    const segIds = new Set();
    conns.forEach(c => {
      segIds.add(`${c.from_segment_kind}:${c.from_segment_id}`);
      segIds.add(`${c.to_segment_kind}:${c.to_segment_id}`);
    });

    if (selectedChain.starting_audience_id) {
      segIds.add(`audience:${selectedChain.starting_audience_id}`);
    }
    if (selectedChain.terminal_deal_id) {
      segIds.add(`deal:${selectedChain.terminal_deal_id}`);
    }

    // Include draft segments not yet wired into a connection so the + button
    // and Outline can spawn nodes that immediately appear on the canvas.
    (workspace.audiences || []).forEach(a => {
      if (a.deleted_at) return;
      if (a.status === 'draft') segIds.add(`audience:${a.id}`);
    });
    (workspace.deals || []).forEach(d => {
      if (d.deleted_at) return;
      if (d.status === 'draft') segIds.add(`deal:${d.id}`);
    });
    (workspace.steps || []).forEach(s => {
      if (s.deleted_at) return;
      if (s.status === 'draft') segIds.add(`step:${s.id}`);
    });

    const nodeRefs = new Map();
    segIds.forEach(key => {
      const [kind, id] = key.split(':');
      let ref = null;
      if (kind === 'audience') ref = workspace.audiences.find(a => a.id === id);
      else if (kind === 'deal') ref = workspace.deals.find(d => d.id === id);
      else if (kind === 'step') ref = workspace.steps.find(s => s.id === id);
      if (!ref) return;
      nodeRefs.set(key, { kind, id, ref });

      if (kind === 'step' && ref.parent_step_id) {
        const parentExpanded = expandedSteps.has(ref.parent_step_id);
        if (!parentExpanded) return;
      }

      g.setNode(key, { width: NODE_W, height: NODE_H, kind, id, ref });

      if (kind === 'step' && ref.parent_step_id) {
        const parentKey = `step:${ref.parent_step_id}`;
        if (segIds.has(parentKey)) g.setParent(key, parentKey);
      }
    });

    conns.forEach(c => {
      const fromKey = `${c.from_segment_kind}:${c.from_segment_id}`;
      const toKey = `${c.to_segment_kind}:${c.to_segment_id}`;
      if (g.hasNode(fromKey) && g.hasNode(toKey)) {
        g.setEdge(fromKey, toKey, { kind: edgeKind(c) });
      }
    });

    try {
      dagre.layout(g);
    } catch (e) {
      console.warn('dagre layout failed', e);
    }

    const nodes = [];
    g.nodes().forEach(key => {
      const n = g.node(key);
      if (!n) return;
      const meta = nodeRefs.get(key);
      if (!meta) return;

      const ref = meta.ref;
      const x = (ref.manual_position_x != null) ? ref.manual_position_x : (n.x - NODE_W / 2);
      const y = (ref.manual_position_y != null) ? ref.manual_position_y : (n.y - NODE_H / 2);

      const hasChildren = meta.kind === 'step' && workspace.steps.some(s => s.parent_step_id === meta.id);

      nodes.push({
        key, kind: meta.kind, id: meta.id, ref,
        x, y, w: NODE_W, h: NODE_H,
        hasChildren,
        expanded: hasChildren && expandedSteps.has(meta.id)
      });
    });

    const edges = [];
    g.edges().forEach(eo => {
      const fromNode = nodes.find(n => n.key === eo.v);
      const toNode = nodes.find(n => n.key === eo.w);
      if (!fromNode || !toNode) return;
      const ed = g.edge(eo);
      edges.push({ from: fromNode, to: toNode, kind: ed?.kind || 'sequence' });
    });

    const allX = nodes.map(n => n.x + n.w);
    const allY = nodes.map(n => n.y + n.h);
    const width = nodes.length ? Math.max(...allX) + 80 : 800;
    const height = nodes.length ? Math.max(...allY) + 80 : 600;

    return { nodes, edges, width, height };
  });

  function startPan(e) {
    if (e.target.closest('.node-clickable')) return;
    if (e.target.closest('.add-btn')) return;
    if (e.target.closest('.toggle-children')) return;
    if (e.target.closest('.add-root')) return;

    // If placing a draft segment, click-to-place
    if (placingSegment) {
      const svg = e.currentTarget.querySelector('svg');
      if (svg) {
        const pt = svgToWorld(svg, e.clientX, e.clientY);
        relocateDraftSegment(placingSegment, pt.x, pt.y);
      }
      return;
    }

    dragging = true;
    dragStart = { x: e.clientX, y: e.clientY };
    panStart = { ...pan };
  }
  function doPan(e) {
    if (pendingNodeAction || draggingNode) return doNodeDrag(e);
    if (!dragging) return;
    pan = { x: panStart.x + (e.clientX - dragStart.x), y: panStart.y + (e.clientY - dragStart.y) };
  }
  function endPan(e) {
    if (pendingNodeAction || draggingNode) return endNodeDrag(e);
    dragging = false;
  }
  function onWheel(e) {
    e.preventDefault();
    const factor = e.deltaY > 0 ? 0.95 : 1.05;
    zoom = Math.max(0.3, Math.min(2.5, zoom * factor));
  }

  function fitToView() {
    if (!layout.nodes.length) { pan = { x: 0, y: 0 }; zoom = 1; return; }
    const padding = 80;
    const minX = Math.min(...layout.nodes.map(n => n.x));
    const minY = Math.min(...layout.nodes.map(n => n.y));
    const maxX = Math.max(...layout.nodes.map(n => n.x + n.w));
    const maxY = Math.max(...layout.nodes.map(n => n.y + n.h));
    const wrap = document.querySelector('.canvas-wrap');
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const availW = Math.max(200, rect.width - padding * 2);
    const availH = Math.max(200, rect.height - padding * 2);
    const contentW = maxX - minX;
    const contentH = maxY - minY;
    const fit = Math.min(availW / contentW, availH / contentH, 1.5);
    zoom = Math.max(0.3, fit);
    pan = {
      x: padding - minX * zoom + (availW - contentW * zoom) / 2,
      y: padding - minY * zoom + (availH - contentH * zoom) / 2
    };
  }

  // Click vs drag — pendingNodeAction tracks below-threshold motion
  function startNodeDrag(node, e) {
    e.stopPropagation();
    e.preventDefault();
    pendingNodeAction = {
      node,
      startClientX: e.clientX,
      startClientY: e.clientY,
      nodeX: node.x,
      nodeY: node.y,
      started: false
    };
  }

  function svgToWorld(svg, clientX, clientY) {
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return {
      x: (clientX - rect.left - pan.x) / zoom,
      y: (clientY - rect.top - pan.y) / zoom
    };
  }

  function doNodeDrag(e) {
    if (pendingNodeAction && !pendingNodeAction.started) {
      const dx = e.clientX - pendingNodeAction.startClientX;
      const dy = e.clientY - pendingNodeAction.startClientY;
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      // Promote to a real drag
      const { node } = pendingNodeAction;
      const svg = document.querySelector('.canvas-wrap svg');
      const pt = svgToWorld(svg, e.clientX, e.clientY);
      draggingNode = {
        id: node.id, kind: node.kind,
        offsetX: pt.x - node.x, offsetY: pt.y - node.y,
        currentX: node.x, currentY: node.y
      };
      // If in selectMode and the dragged node is part of a multi-selection,
      // snapshot the start positions of the OTHER selected nodes so we can
      // translate them by the same delta during drag.
      if (selectMode && selectedSegments.length > 1 && isSelected(node.kind, node.id)) {
        const others = selectedSegments
          .filter(s => !(s.kind === node.kind && s.id === node.id))
          .map(s => {
            const n = layout.nodes.find(x => x.kind === s.kind && x.id === s.id);
            if (!n) return null;
            return { kind: s.kind, id: s.id, startX: n.x, startY: n.y };
          })
          .filter(Boolean);
        multiMoveSnapshot = {
          anchorId: node.id,
          anchorStartX: node.x,
          anchorStartY: node.y,
          others
        };
      } else {
        multiMoveSnapshot = null;
      }
      pendingNodeAction.started = true;
    }
    if (!draggingNode) return;
    const svg = document.querySelector('.canvas-wrap svg');
    const pt = svgToWorld(svg, e.clientX, e.clientY);
    draggingNode.currentX = pt.x - draggingNode.offsetX;
    draggingNode.currentY = pt.y - draggingNode.offsetY;
    const node = layout.nodes.find(n => n.id === draggingNode.id);
    if (node) {
      node.x = draggingNode.currentX;
      node.y = draggingNode.currentY;
    }

    // Multi-select group move: translate other selected nodes by the same delta
    if (multiMoveSnapshot && node) {
      const dx = node.x - multiMoveSnapshot.anchorStartX;
      const dy = node.y - multiMoveSnapshot.anchorStartY;
      for (const o of multiMoveSnapshot.others) {
        const target = layout.nodes.find(n => n.kind === o.kind && n.id === o.id);
        if (target) {
          target.x = o.startX + dx;
          target.y = o.startY + dy;
        }
      }
    }

    // Drag-merge detection — only when dragging a step
    if (draggingNode.kind === 'step' && node) {
      const cx = draggingNode.currentX + node.w / 2;
      const cy = draggingNode.currentY + node.h / 2;
      const target = layout.nodes.find(n =>
        n.kind === 'step' &&
        n.id !== draggingNode.id &&
        cx >= n.x && cx <= n.x + n.w &&
        cy >= n.y && cy <= n.y + n.h
      );
      if (target) {
        if (mergeTargetId !== target.id) {
          mergeTargetId = target.id;
          mergeHoverStart = performance.now();
          mergeTargetReady = false;
          if (mergeTimerRaf) cancelAnimationFrame(mergeTimerRaf);
          mergeTimerRaf = requestAnimationFrame(tickMergeReady);
        }
      } else {
        if (mergeTargetId) clearMergeState();
      }
    }
  }

  async function endNodeDrag(e) {
    // No drag started → it was a click
    if (pendingNodeAction && !pendingNodeAction.started) {
      const node = pendingNodeAction.node;
      pendingNodeAction = null;
      if (selectMode) {
        toggleSegmentSelection(node.kind, node.id);
        return;
      }
      if (perspectiveMode) {
        togglePerspectivePick(node.kind, node.id);
        return;
      }
      if (arrowMode) {
        await handleArrowSegmentClick(node);
        return;
      }
      selectedSegment = { kind: node.kind, id: node.id };
      return;
    }
    pendingNodeAction = null;
    if (!draggingNode) {
      multiMoveSnapshot = null;
      return;
    }
    const { id, kind, currentX, currentY } = draggingNode;
    const draggedNode = layout.nodes.find(n => n.id === id);
    const draggedW = draggedNode?.w || 0;
    const draggedH = draggedNode?.h || 0;
    const capturedMergeTargetId = mergeTargetId;
    const capturedMergeReady = mergeTargetReady;
    draggingNode = null;
    const table = kind === 'audience' ? 'bb_audiences' : kind === 'deal' ? 'bb_deals' : 'bb_steps';
    try {
      await supabase.from(table).update({
        manual_position_x: Math.round(currentX),
        manual_position_y: Math.round(currentY)
      }).eq('id', id);
    } catch (e) { console.warn('save position failed', e); }

    // Multi-select group move: persist new positions for the OTHER selected nodes
    if (multiMoveSnapshot) {
      const snapshot = multiMoveSnapshot;
      multiMoveSnapshot = null;
      const dx = currentX - snapshot.anchorStartX;
      const dy = currentY - snapshot.anchorStartY;
      for (const o of snapshot.others) {
        const otherTable = o.kind === 'audience' ? 'bb_audiences' : o.kind === 'deal' ? 'bb_deals' : 'bb_steps';
        try {
          await supabase.from(otherTable).update({
            manual_position_x: Math.round(o.startX + dx),
            manual_position_y: Math.round(o.startY + dy)
          }).eq('id', o.id);
        } catch (e) { console.warn('save group position failed', o, e); }
      }
      if (snapshot.others.length > 0) {
        showToast(`Moved ${snapshot.others.length + 1} segments`, 'success');
      }
    }

    // Drag-merge / drag-split — only for steps
    if (kind === 'step') {
      const draggedStep = workspace.steps.find(s => s.id === id);
      if (capturedMergeReady && capturedMergeTargetId && capturedMergeTargetId !== id) {
        try {
          const { error } = await supabase.from('bb_steps')
            .update({ parent_step_id: capturedMergeTargetId })
            .eq('id', id);
          if (error) throw error;
          showToast('Nested as child step', 'success');
        } catch (e) { showToast('Nest failed: ' + e.message, 'error'); }
      } else if (draggedStep?.parent_step_id) {
        const parentNode = layout.nodes.find(n => n.kind === 'step' && n.id === draggedStep.parent_step_id);
        if (parentNode) {
          const cx = currentX + draggedW / 2;
          const cy = currentY + draggedH / 2;
          const outside =
            cx < parentNode.x - SPLIT_BUFFER ||
            cx > parentNode.x + parentNode.w + SPLIT_BUFFER ||
            cy < parentNode.y - SPLIT_BUFFER ||
            cy > parentNode.y + parentNode.h + SPLIT_BUFFER;
          if (outside) {
            try {
              const { error } = await supabase.from('bb_steps')
                .update({ parent_step_id: null })
                .eq('id', id);
              if (error) throw error;
              showToast('Unlinked from parent', 'success');
            } catch (e) { showToast('Unlink failed: ' + e.message, 'error'); }
          }
        }
      }
    }

    clearMergeState();
    await refreshWorkspace();
  }

  function toggleChildren(stepId, e) {
    e.stopPropagation();
    const next = new Set(expandedSteps);
    if (next.has(stepId)) next.delete(stepId);
    else next.add(stepId);
    expandedSteps = next;
  }

  async function runAudit() {
    if (!selectedChain) return;
    auditResult = null; auditLoading = true;
    try {
      const r = await callBbAi('audit', { scope: 'chain', scope_id: selectedChain.id });
      auditResult = r;
    } catch (e) {
      auditResult = { summary: 'Audit failed: ' + e.message, findings: [] };
    } finally { auditLoading = false; }
  }

  async function handleMenuAdd({ kind }) {
    if (kind === 'chain') {
      chainBuilderInitial = { audienceId: null, dealId: null };
      showChainBuilder = true;
      return;
    }
    if (kind === 'arrow') {
      arrowMode = { firstId: null, firstKind: null };
      showToast('Click a segment to start the arrow', 'info');
      return;
    }
    if (kind === 'audience' || kind === 'step' || kind === 'deal') {
      await spawnDraftSegment(kind);
    }
  }

  async function spawnDraftSegment(kind) {
    const wrap = document.querySelector('.canvas-wrap');
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    // Spawn near the bottom-left + button in screen coords, then convert to world
    const screenX = 100;
    const screenY = rect.height - 100;
    const worldX = (screenX - pan.x) / zoom;
    const worldY = (screenY - pan.y) / zoom;

    let table, payload;
    if (kind === 'audience') {
      table = 'bb_audiences';
      payload = {
        workspace_id: workspace.id,
        name: 'New audience',
        status: 'draft',
        manual_position_x: Math.round(worldX),
        manual_position_y: Math.round(worldY)
      };
    } else if (kind === 'deal') {
      // A new offer on a company map is a new HQ offer (lib/offers.js).
      try {
        const d = await createOffer({ name: 'New offer' }, { manual_position_x: Math.round(worldX), manual_position_y: Math.round(worldY) });
        await refreshWorkspace();
        placingSegment = { kind, draftId: d.id };
        showToast('Offer placed — click anywhere on the canvas to reposition, or click the card to edit', 'info');
      } catch (e) {
        showToast(`Failed to add offer: ${e?.message || 'unknown error'}`, 'error');
      }
      return;
    } else {
      table = 'bb_steps';
      payload = {
        workspace_id: workspace.id,
        step_name: 'New step',
        status: 'draft',
        state: 'user',
        manual_position_x: Math.round(worldX),
        manual_position_y: Math.round(worldY)
      };
    }

    console.log('[spawnDraftSegment] inserting', { kind, table, payload });
    try {
      const { data, error } = await supabase.from(table).insert(payload).select().single();
      if (error) {
        console.error('[spawnDraftSegment] supabase error:', error);
        throw error;
      }
      console.log('[spawnDraftSegment] inserted', data);
      await refreshWorkspace();
      placingSegment = { kind, draftId: data.id };
      showToast(`${kind} placed — click anywhere on the canvas to reposition, or click the card to edit`, 'info');
    } catch (e) {
      console.error('[spawnDraftSegment] failed:', e);
      showToast(`Failed to add ${kind}: ${e?.message || 'unknown error — see console'}`, 'error');
    }
  }

  async function relocateDraftSegment(seg, x, y) {
    const table = seg.kind === 'audience' ? 'bb_audiences' : seg.kind === 'deal' ? 'bb_deals' : 'bb_steps';
    try {
      await supabase.from(table).update({
        manual_position_x: Math.round(x),
        manual_position_y: Math.round(y)
      }).eq('id', seg.draftId);
      await refreshWorkspace();
      placingSegment = null;
      showToast('Placed — click the card to edit details', 'success');
    } catch (e) {
      showToast('Place failed: ' + e.message, 'error');
    }
  }

  async function handleArrowSegmentClick(node) {
    if (!arrowMode) return false;
    if (!arrowMode.firstId) {
      arrowMode = { firstId: node.id, firstKind: node.kind };
      showToast('Now click the second segment', 'info');
      return true;
    }
    if (arrowMode.firstId === node.id) {
      showToast('Pick a different segment', 'error');
      return true;
    }
    if (arrowMode.firstKind === 'deal' && node.kind === 'audience') {
      showToast('Audience cannot come after an offer', 'error');
      arrowMode = null;
      return true;
    }
    try {
      const { error } = await supabase.from('bb_segment_connections').insert({
        workspace_id: workspace.id,
        from_segment_id: arrowMode.firstId,
        from_segment_kind: arrowMode.firstKind,
        to_segment_id: node.id,
        to_segment_kind: node.kind,
        chain_id: selectedChain?.id || null
      });
      if (error) throw error;
      await refreshWorkspace();
      showToast('Connection drawn', 'success');
    } catch (e) {
      showToast('Connection failed: ' + e.message, 'error');
    }
    arrowMode = null;
    return true;
  }

  $effect(() => {
    const handler = (e) => {
      chainBuilderInitial = {
        audienceId: e.detail?.audienceId || null,
        dealId: e.detail?.dealId || null
      };
      showChainBuilder = true;
    };
    window.addEventListener('open-chain-builder', handler);
    return () => window.removeEventListener('open-chain-builder', handler);
  });

  $effect(() => {
    const handler = (e) => {
      if (e.detail?.id) {
        selectedSegment = { kind: 'step', id: e.detail.id };
      }
    };
    window.addEventListener('open-step-editor', handler);
    return () => window.removeEventListener('open-step-editor', handler);
  });

  $effect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (placingSegment) { placingSegment = null; showToast('Placing cancelled', 'info'); return; }
        if (arrowMode) { arrowMode = null; showToast('Arrow mode cancelled', 'info'); return; }
        if (perspectiveMode) { togglePerspectiveMode(); showToast('Perspective off', 'info'); return; }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  function fmtPath(from, to) {
    const x1 = from.x + from.w / 2, y1 = from.y + from.h;
    const x2 = to.x + to.w / 2, y2 = to.y;
    const midY = (y1 + y2) / 2;
    return `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;
  }

  function costValueBar(cost, value) {
    const safe = (v) => Math.max(0, Math.min(10, v ?? 0));
    return { cost: safe(cost), value: safe(value), warn: safe(cost) > safe(value) };
  }
</script>

<div class="map-view">
  {#if techMode.mapMode === 'normal'}
  <div class="map-header">
    <h2>Map</h2>
    {#if workspace.chains.length > 1}
      <select bind:value={selectedChainId}>
        {#each workspace.chains as c}
          <option value={c.id}>{c.name}</option>
        {/each}
      </select>
    {/if}
    {#if selectedChain}
      <button class="audit-btn" onclick={runAudit} disabled={auditLoading} type="button">
        {auditLoading ? 'Auditing…' : '🔎 Audit chain'}
      </button>
    {/if}
    <button class="audit-btn outline-btn" onclick={() => outlineModalOpen = true} type="button">✎ Outline</button>
    <button class="audit-btn select-btn" class:active={selectMode} onclick={toggleSelectMode} type="button">
      {selectMode ? `☑ Select (${selectedSegments.length})` : '☐ Select'}
    </button>
    {#if selectMode && selectedSegments.length > 0}
      <button class="audit-btn delete-btn" onclick={deleteSelectedSegments} disabled={deletingSelected} type="button">
        {deletingSelected ? 'Deleting…' : `🗑 Delete (${selectedSegments.length})`}
      </button>
    {/if}
    <div class="zoom-controls">
      <button onclick={() => zoom = Math.max(0.3, zoom * 0.95)} type="button">−</button>
      <span>{Math.round(zoom * 100)}%</span>
      <button onclick={() => zoom = Math.min(2.5, zoom * 1.05)} type="button">+</button>
      <button onclick={fitToView} type="button">Fit</button>
      <button onclick={() => { pan = { x: 0, y: 0 }; zoom = 1; }} type="button">Reset</button>
      <button
        class="perspective-btn"
        class:active={perspectiveMode}
        onclick={togglePerspectiveMode}
        type="button"
        title="Perspective: filter graph to paths through up to 3 selected segments"
      >
        {perspectiveMode ? '◉' : '○'} Perspective
      </button>
    </div>
  </div>

  {#if auditResult}
    <div class="audit-card">
      <div class="audit-summary">{auditResult.summary}</div>
      {#if auditResult.findings?.length}
        <ul class="findings">
          {#each auditResult.findings as f}
            <li class="finding finding-{f.severity}">
              <span class="sev">{f.severity}</span>
              <div>
                <div><strong>{f.area}:</strong> {f.finding}</div>
                {#if f.suggestion}<div class="suggest">{f.suggestion}</div>{/if}
              </div>
            </li>
          {/each}
        </ul>
      {/if}
      <button class="audit-close" onclick={() => auditResult = null} type="button">Close</button>
    </div>
  {/if}

  {#if workspace.chains.length === 0}
    <div class="canvas-wrap empty-canvas">
      <div class="map-empty-startguide">
        <svg width="180" height="120" viewBox="0 0 180 120">
          <path d="M 160 20 Q 120 40, 80 60 T 30 100" stroke="#d4af37" stroke-width="2.5" fill="none" stroke-dasharray="6 6" />
          <polygon points="20,108 32,98 36,112" fill="#d4af37" />
        </svg>
        <div class="start-label">Start here</div>
      </div>
    </div>
  {:else if selectedChain}
    <div class="canvas-wrap"
         onmousedown={startPan}
         onmousemove={doPan}
         onmouseup={endPan}
         onmouseleave={endPan}
         onwheel={onWheel}>
      <svg width={layout.width} height={layout.height}
           style="transform: translate({pan.x}px, {pan.y}px) scale({zoom}); transform-origin: top left;">

        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="rgba(255,255,255,0.4)" />
          </marker>
          <marker id="arrow-gold" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#d4af37" />
          </marker>
          <marker id="arrow-blue" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#7AB8FF" />
          </marker>
        </defs>

        {#each layout.edges as edge}
          {@const fromKey = `${edge.from.kind}:${edge.from.id}`}
          {@const toKey = `${edge.to.kind}:${edge.to.id}`}
          {@const edgeIn = !perspectiveNodeIds || (perspectiveNodeIds.has(fromKey) && perspectiveNodeIds.has(toKey))}
          {@const fromFan = fannedPositions.get(edge.from.id)}
          {@const toFan = fannedPositions.get(edge.to.id)}
          {@const fromNode = fromFan ? { ...edge.from, x: fromFan.x, y: fromFan.y } : edge.from}
          {@const toNode = toFan ? { ...edge.to, x: toFan.x, y: toFan.y } : edge.to}
          <path
            class:dimmed={!edgeIn}
            d={fmtPath(fromNode, toNode)}
            stroke={edge.kind === 'entry' ? '#7AB8FF' : edge.kind === 'exit' ? '#d4af37' : 'rgba(255,255,255,0.35)'}
            stroke-width="2"
            fill="none"
            marker-end={edge.kind === 'entry' ? 'url(#arrow-blue)' : edge.kind === 'exit' ? 'url(#arrow-gold)' : 'url(#arrow)'}
          />
        {/each}

        {#each layout.nodes as node}
          {@const nodeKey = `${node.kind}:${node.id}`}
          {@const nodeIn = !perspectiveNodeIds || perspectiveNodeIds.has(nodeKey)}
          {@const nodeSelected = perspectiveMode && isInPerspective(node.kind, node.id)}
          {@const fanCoord = fannedPositions.get(node.id)}
          {@const nx = fanCoord?.x ?? node.x}
          {@const ny = fanCoord?.y ?? node.y}
          {@const isFanCenter = node.id === hoveredFanCenter}
          <foreignObject
            x={nx}
            y={ny}
            width={node.w}
            height={node.h + 40}
            class="fo-node"
            class:fanned={!!fanCoord}
            class:fan-center={isFanCenter}
          >
            <div
              class="node-clickable {node.kind}-node"
              class:dragging-self={draggingNode?.id === node.id}
              class:dimmed={!nodeIn}
              class:perspective-selected={nodeSelected}
              class:merge-target-pending={mergeTargetId === node.id && !mergeTargetReady}
              class:merge-target-ready={mergeTargetId === node.id && mergeTargetReady}
              onmousedown={(e) => startNodeDrag(node, e)}
              onmouseenter={() => handleNodeMouseEnter(node)}
              onmouseleave={handleNodeMouseLeave}
            >
              <div class="node-kind">{node.kind === 'deal' ? 'offer' : node.kind}</div>
              <strong>
                {node.kind === 'step' ? (node.ref.step_name || 'Untitled') : node.ref.name}
              </strong>

              {#if node.kind === 'audience' && node.ref.want}
                <div class="node-sub">{node.ref.want}</div>
              {:else if node.kind === 'deal' && (node.ref.offer?.promise_text || node.ref.what_it_is)}
                <div class="node-sub">{node.ref.offer?.promise_text || node.ref.what_it_is}</div>
              {:else if node.kind === 'step'}
                {@const bar = costValueBar(node.ref.perceived_cost, node.ref.perceived_value)}
                <div class="cost-value">
                  <div class="cv-row">
                    <span class="cv-label">cost</span>
                    <div class="cv-bar"><div class="cv-fill cv-cost" class:warn={bar.warn} style="width: {bar.cost * 10}%"></div></div>
                    <span class="cv-num">{bar.cost}</span>
                  </div>
                  <div class="cv-row">
                    <span class="cv-label">value</span>
                    <div class="cv-bar"><div class="cv-fill cv-value" style="width: {bar.value * 10}%"></div></div>
                    <span class="cv-num">{bar.value}</span>
                  </div>
                </div>
                {#if node.ref.is_first_value}
                  <div class="first-value-marker" title="First value moment"></div>
                {/if}
                {#if node.ref.needs_help}
                  <div class="needs-help-marker" title={node.ref.helper_role ? `Needs: ${node.ref.helper_role}` : 'Needs help'}>👤</div>
                {/if}
              {/if}

              {#if node.hasChildren}
                <button class="toggle-children" onclick={(e) => toggleChildren(node.id, e)} type="button" title={node.expanded ? 'Collapse children' : 'Expand children'}>
                  {node.expanded ? '⌃' : '⌄'}
                </button>
              {/if}
            </div>
          </foreignObject>
        {/each}
      </svg>
    </div>
  {/if}

  {#if selectedSegment}
    {#if selectedSegment.kind === 'step'}
      <StepEditor id={selectedSegment.id} onClose={() => selectedSegment = null} />
    {:else}
      <EntityEditor kind={selectedSegment.kind} id={selectedSegment.id} onClose={() => selectedSegment = null} />
    {/if}
  {/if}

  <MapAddMenu onAdd={handleMenuAdd} />

  {#if showChainBuilder}
    <ChainBuilder
      initialAudienceId={chainBuilderInitial.audienceId}
      initialDealId={chainBuilderInitial.dealId}
      onClose={() => showChainBuilder = false}
      onChainCreated={(id) => { selectedChainId = id; showChainBuilder = false; }}
    />
  {/if}

  {#if arrowMode}
    <div class="arrow-mode-banner">
      Arrow mode {arrowMode.firstId ? '— click second segment' : '— click first segment'}
      <button onclick={() => arrowMode = null} type="button">Cancel</button>
    </div>
  {/if}

  {#if placingSegment}
    <div class="placing-mode-banner">
      Placing {placingSegment.kind} — click anywhere on the canvas to position, or drag the card. Click the card again to edit.
      <button onclick={() => placingSegment = null} type="button">Done</button>
    </div>
  {/if}

  {#if perspectiveMode}
    <div class="perspective-banner">
      <span>Perspective {perspectiveSelection.length}/3 — click segments to filter</span>
      {#if perspectiveSelection.length > 0}
        <div class="perspective-pills">
          {#each perspectiveSelection as p}
            {@const ref = p.kind === 'audience'
              ? workspace.audiences.find(a => a.id === p.id)
              : p.kind === 'deal'
                ? workspace.deals.find(d => d.id === p.id)
                : workspace.steps.find(s => s.id === p.id)}
            <button class="perspective-pill" onclick={() => togglePerspectivePick(p.kind, p.id)} type="button">
              {ref?.name || ref?.step_name || '(unknown)'} ✕
            </button>
          {/each}
        </div>
      {/if}
      {#if perspectiveSelection.length > 0}
        <button class="perspective-clear" onclick={() => perspectiveSelection = []} type="button">Clear</button>
      {/if}
      <button class="perspective-exit" onclick={togglePerspectiveMode} type="button">Done</button>
    </div>
  {/if}

  {#if outlineModalOpen}
    <div class="outline-overlay" onclick={() => outlineModalOpen = false}>
      <div class="outline-modal" onclick={(e) => e.stopPropagation()}>
        <div class="outline-head">
          <div>
            <h3>Outline</h3>
            <p>Describe the segments and how they connect. Default kind is step.</p>
          </div>
          <button class="outline-close" onclick={() => outlineModalOpen = false} type="button">×</button>
        </div>
        <textarea
          class="outline-textarea"
          bind:value={outlineText}
          placeholder={"x leads to y leads to z\nb and d are also connected to z"}
          rows="8"
          disabled={outlineLoading}
        ></textarea>
        <div class="outline-hint">Plain language. Examples: "x → y → z", "x leads to y", "b and d both connect to z". AI parses it into segments + arrows.</div>
        <div class="outline-actions">
          <button class="outline-cancel" onclick={() => outlineModalOpen = false} type="button">Cancel</button>
          <button class="outline-submit" onclick={submitOutline} disabled={outlineLoading || !outlineText.trim()} type="button">
            {outlineLoading ? 'Building…' : 'Build on map'}
          </button>
        </div>
      </div>
    </div>
  {/if}
  {:else}
    <TechMapShell />
  {/if}
</div>

<style>
  .map-view { position: relative; height: calc(100vh - 100px); display: flex; flex-direction: column; }
  .map-header { display: flex; align-items: center; gap: 12px; padding: 12px 24px; border-bottom: 1px solid rgba(255,255,255,0.06); flex-wrap: wrap; }
  .map-header h2 { font-family: 'DM Serif Display', serif; font-weight: 400; font-size: 26px; color: #f0efe9; margin: 0; letter-spacing: -0.01em; }
  .map-header select { background: rgba(0,0,0,0.4); border: 1px solid rgba(255,255,255,0.1); color: #f0efe9; padding: 5px 10px; border-radius: 6px; font-size: 12px; }
  .audit-btn { background: rgba(212,175,55,0.12); border: 1px solid rgba(212,175,55,0.3); color: #d4af37; padding: 6px 14px; border-radius: 999px; cursor: pointer; font-size: 12px; font-weight: 500; margin: 0; }
  .audit-btn:hover:not(:disabled) { background: rgba(212,175,55,0.2); }
  .outline-btn { background: rgba(181,131,240,0.12); border-color: rgba(181,131,240,0.4); color: #B583F0; }
  .outline-btn:hover { background: rgba(181,131,240,0.2); }
  .select-btn { background: rgba(122,184,255,0.1); border-color: rgba(122,184,255,0.35); color: #7AB8FF; }
  .select-btn:hover { background: rgba(122,184,255,0.18); }
  .select-btn.active { background: rgba(122,184,255,0.25); border-color: #7AB8FF; }
  .delete-btn { background: rgba(212,80,80,0.15); border-color: rgba(212,80,80,0.45); color: #ff8585; }
  .delete-btn:hover:not(:disabled) { background: rgba(212,80,80,0.28); }

  .outline-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); backdrop-filter: blur(6px); z-index: 200; display: flex; align-items: center; justify-content: center; padding: 20px; }
  .outline-modal { width: 100%; max-width: 540px; background: linear-gradient(180deg, rgba(20,18,22,0.98), rgba(12,11,14,0.98)); border: 1px solid rgba(181,131,240,0.35); border-radius: 16px; padding: 22px; box-shadow: 0 24px 60px rgba(0,0,0,0.6); }
  .outline-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 14px; gap: 14px; }
  .outline-head h3 { margin: 0; font-family: 'DM Serif Display', serif; font-size: 22px; color: #f0efe9; letter-spacing: -0.01em; }
  .outline-head p { margin: 4px 0 0; font-size: 12px; color: #9aa0a6; }
  .outline-close { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); color: #b8b8bc; width: 32px; height: 32px; border-radius: 50%; cursor: pointer; font-size: 18px; line-height: 1; flex-shrink: 0; }
  .outline-textarea { width: 100%; box-sizing: border-box; background: rgba(0,0,0,0.4); border: 1px solid rgba(181,131,240,0.3); color: #f4f4f6; padding: 12px 14px; border-radius: 10px; font-size: 14px; line-height: 1.5; font-family: inherit; resize: vertical; min-height: 140px; }
  .outline-textarea:focus { outline: none; border-color: rgba(181,131,240,0.6); }
  .outline-hint { color: #6a6a6e; font-size: 11px; margin: 8px 0 14px; line-height: 1.5; font-style: italic; }
  .outline-actions { display: flex; justify-content: flex-end; gap: 8px; }
  .outline-cancel { background: transparent; color: #b8b8bc; border: 1px solid rgba(255,255,255,0.15); padding: 9px 18px; border-radius: 8px; cursor: pointer; font-size: 13px; }
  .outline-submit { background: linear-gradient(180deg, #B583F0, #7C4DC9); color: #0a0a0c; border: none; padding: 9px 22px; border-radius: 8px; cursor: pointer; font-weight: 700; font-size: 13px; box-shadow: inset 0 1px 0 rgba(255,255,255,0.3); }
  .outline-submit:disabled { opacity: 0.45; cursor: not-allowed; }
  .zoom-controls { margin-left: auto; display: flex; gap: 6px; align-items: center; }
  .zoom-controls button { background: rgba(0,0,0,0.4); border: 1px solid rgba(255,255,255,0.1); color: #f0efe9; padding: 4px 10px; border-radius: 4px; cursor: pointer; }
  .zoom-controls span { color: #888; font-size: 11px; min-width: 40px; text-align: center; font-family: 'JetBrains Mono', monospace; }

  .audit-card { margin: 12px 24px; padding: 14px 16px; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; }
  .audit-summary { color: #f0efe9; font-style: italic; margin-bottom: 10px; line-height: 1.5; }
  .findings { list-style: none; padding: 0; margin: 0 0 10px; display: flex; flex-direction: column; gap: 6px; }
  .finding { display: flex; gap: 10px; padding: 8px 12px; background: rgba(255,255,255,0.02); border-left: 3px solid; border-radius: 6px; }
  .finding-red { border-left-color: #d48484; }
  .finding-amber { border-left-color: #d4af37; }
  .finding-green { border-left-color: #7cd992; }
  .sev { font-family: 'JetBrains Mono', monospace; font-size: 9px; text-transform: uppercase; letter-spacing: 1.2px; padding: 2px 8px; border-radius: 4px; flex-shrink: 0; }
  .finding-red .sev { background: rgba(212,100,100,0.2); color: #d48484; }
  .finding-amber .sev { background: rgba(212,175,55,0.2); color: #d4af37; }
  .finding-green .sev { background: rgba(124,217,146,0.2); color: #7cd992; }
  .finding div { color: #d8d8dc; font-size: 12px; line-height: 1.5; }
  .suggest { color: #9aa0a6; font-style: italic; margin-top: 4px; }
  .audit-close { background: rgba(255,255,255,0.04); color: #b8b8bc; border: 1px solid rgba(255,255,255,0.1); padding: 6px 14px; border-radius: 6px; cursor: pointer; font-size: 11px; margin: 0; }

  .canvas-wrap { flex: 1; position: relative; overflow: hidden; background: rgba(0,0,0,0.2); cursor: grab; }
  .canvas-wrap:active { cursor: grabbing; }
  .canvas-wrap.empty-canvas { cursor: default; }
  svg { display: block; }

  .node-clickable { width: 100%; height: 96px; padding: 10px 14px; border-radius: 10px; background: linear-gradient(180deg, rgba(20,18,22,0.95), rgba(12,11,14,0.95)); border: 1px solid rgba(255,255,255,0.12); position: relative; box-sizing: border-box; cursor: grab; user-select: none; transition: border-color 0.15s ease, box-shadow 0.15s ease; }
  .node-clickable:hover { border-color: rgba(212,175,55,0.4); }
  .node-clickable:active, .node-clickable.dragging-self { cursor: grabbing; box-shadow: 0 8px 24px rgba(0,0,0,0.6); border-color: rgba(212,175,55,0.6); }
  .node-kind { font-family: 'JetBrains Mono', monospace; font-size: 9px; color: #888; text-transform: uppercase; letter-spacing: 1.2px; }
  .node-clickable strong { color: #f0efe9; font-size: 13px; display: block; margin-top: 2px; line-height: 1.3; }
  .node-sub { color: #d8d8dc; font-size: 11px; margin-top: 4px; line-height: 1.4; max-height: 28px; overflow: hidden; }
  .audience-node { border-color: rgba(122,184,255,0.45) !important; }
  .deal-node { border-color: rgba(212,175,55,0.5) !important; }

  .cost-value { margin-top: 6px; display: flex; flex-direction: column; gap: 3px; }
  .cv-row { display: flex; align-items: center; gap: 6px; font-family: 'JetBrains Mono', monospace; font-size: 9px; }
  .cv-label { color: rgba(255,255,255,0.5); width: 30px; text-transform: uppercase; letter-spacing: 0.4px; }
  .cv-bar { flex: 1; height: 4px; background: rgba(255,255,255,0.06); border-radius: 2px; overflow: hidden; }
  .cv-fill { height: 100%; transition: width 0.2s ease; }
  .cv-cost { background: rgba(122,184,255,0.7); }
  .cv-cost.warn { background: rgba(220,100,100,0.85); }
  .cv-value { background: rgba(124,217,146,0.7); }
  .cv-num { color: rgba(255,255,255,0.7); width: 14px; text-align: right; }

  .first-value-marker { position: absolute; top: -3px; right: -3px; width: 12px; height: 12px; border-radius: 50%; background: radial-gradient(circle, #d4af37, #a8861a); box-shadow: 0 0 12px rgba(212,175,55,0.6); }

  .needs-help-marker {
    position: absolute;
    top: -3px;
    left: -3px;
    width: 20px; height: 20px;
    border-radius: 50%;
    background: rgba(122, 184, 255, 0.95);
    color: #0a0a0c;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px;
    box-shadow: 0 0 10px rgba(122, 184, 255, 0.5);
    border: 1.5px solid #0a0a0c;
  }

  .toggle-children { position: absolute; bottom: -4px; left: 50%; transform: translateX(-50%); width: 22px; height: 14px; border-radius: 7px; background: rgba(20,18,22,0.95); border: 1px solid rgba(212,175,55,0.4); color: #d4af37; cursor: pointer; font-size: 10px; line-height: 1; padding: 0; display: flex; align-items: center; justify-content: center; }
  .toggle-children:hover { background: rgba(212,175,55,0.2); }

  .map-empty-startguide { position: absolute; bottom: 88px; left: 64px; pointer-events: none; display: flex; flex-direction: column; align-items: center; }
  .start-label { font-family: 'DM Serif Display', serif; font-size: 18px; color: #d4af37; margin-top: 4px; }

  .arrow-mode-banner {
    position: absolute;
    top: 16px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(122,184,255,0.15);
    border: 1px solid rgba(122,184,255,0.4);
    color: #7AB8FF;
    padding: 8px 16px;
    border-radius: 999px;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    z-index: 5;
  }
  .arrow-mode-banner button {
    background: rgba(255,255,255,0.06);
    color: #b8b8bc;
    border: 1px solid rgba(255,255,255,0.1);
    padding: 4px 10px;
    border-radius: 999px;
    cursor: pointer;
    font-size: 11px;
  }

  .placing-mode-banner {
    position: absolute;
    top: 16px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(212, 175, 55, 0.15);
    border: 1px solid rgba(212, 175, 55, 0.4);
    color: #d4af37;
    padding: 8px 16px;
    border-radius: 999px;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    z-index: 5;
    max-width: 540px;
    line-height: 1.4;
  }
  .placing-mode-banner button {
    background: rgba(255,255,255,0.06);
    color: #b8b8bc;
    border: 1px solid rgba(255,255,255,0.1);
    padding: 4px 10px;
    border-radius: 999px;
    cursor: pointer;
    font-size: 11px;
    flex-shrink: 0;
  }

  /* Perspective view */
  .perspective-btn {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    letter-spacing: 0.5px;
  }
  .perspective-btn.active {
    background: rgba(122,184,255,0.15) !important;
    color: #7AB8FF !important;
    border-color: rgba(122,184,255,0.4) !important;
  }
  .node-clickable.dimmed { opacity: 0.18; pointer-events: none; }
  .node-clickable.perspective-selected {
    border-color: rgba(122,184,255,0.7) !important;
    box-shadow: 0 0 18px rgba(122,184,255,0.45) !important;
  }
  path.dimmed { opacity: 0.12; }

  .perspective-banner {
    position: absolute;
    top: 16px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(122,184,255,0.12);
    border: 1px solid rgba(122,184,255,0.35);
    color: #7AB8FF;
    padding: 8px 16px;
    border-radius: 16px;
    font-size: 12px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    z-index: 5;
    max-width: 720px;
  }
  .perspective-pills { display: flex; gap: 4px; flex-wrap: wrap; }
  .perspective-pill {
    background: rgba(122,184,255,0.2);
    border: 1px solid rgba(122,184,255,0.4);
    color: #7AB8FF;
    padding: 3px 8px;
    border-radius: 999px;
    cursor: pointer;
    font-size: 10px;
    margin: 0;
  }
  .perspective-pill:hover { background: rgba(122,184,255,0.3); }
  .perspective-clear, .perspective-exit {
    background: rgba(255,255,255,0.05);
    color: #b8b8bc;
    border: 1px solid rgba(255,255,255,0.1);
    padding: 3px 10px;
    border-radius: 999px;
    cursor: pointer;
    font-size: 10px;
    margin: 0;
  }
  .perspective-clear:hover, .perspective-exit:hover { color: #fff; }

  /* Fan-on-hover */
  .fo-node {
    transition: x 200ms cubic-bezier(0.4, 0, 0.2, 1), y 200ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  .fo-node.fan-center .node-clickable {
    box-shadow: 0 0 24px rgba(212,175,55,0.55), 0 8px 24px rgba(0,0,0,0.6);
    border-color: rgba(212,175,55,0.7);
  }

  /* Drag-to-merge highlight */
  .node-clickable.merge-target-pending {
    border-color: rgba(122,184,255,0.5) !important;
    box-shadow: 0 0 12px rgba(122,184,255,0.4);
    animation: merge-pending-pulse 1s ease-in-out infinite;
  }
  .node-clickable.merge-target-ready {
    border: 2px dashed #7AB8FF !important;
    box-shadow: 0 0 24px rgba(122,184,255,0.7), inset 0 0 16px rgba(122,184,255,0.2);
  }
  @keyframes merge-pending-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.78; }
  }
</style>
